import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Loader2, Check, X, Info, AlertTriangle } from 'lucide-react';
import { FORMATS, PRICES } from '../config/prices';
import { useToast } from "./use-toast";

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Largest print size offered to users. Mirrors backend `MAX_PRINT_SIZE`
// from config.py so the UI doesn't expose sizes the backend won't probe.
const ALL_SIZES_ASC = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];
const _maxIdx = ALL_SIZES_ASC.indexOf(process.env.REACT_APP_MAX_PRINT_SIZE || 'L');
const ALLOWED_SIZES = ALL_SIZES_ASC.slice(0, _maxIdx === -1 ? ALL_SIZES_ASC.length : _maxIdx + 1);

// Add blinking animation CSS
const blinkingStyle = `
@keyframes blink {
  0%, 10%   { opacity: 1; }
  50%       { opacity: 0.3; }
  90%, 100% { opacity: 1; }
}
.blink-animation {
  animation: blink 1.6s ease-in-out infinite;
}
`;


const MapPurchaseSection = ({
    finalImage,
    maxDpis,
    selectedFormat,
    onSizeSelect,
    isUnlocking,
    imageId,
    isPortrait,
    km2,
    voucher: setVoucherCode,
    onClose // Add this prop for closing the popup
}) => {
    const { toast } = useToast();
    const [selectedSize, setSelectedSize] = useState("XXS");
    const [isApplyingVoucher, setIsApplyingVoucher] = useState(false);
    const [voucherDiscount, setVoucherDiscount] = useState(null);
    const [voucherDiscountType, setVoucherDiscountType] = useState(null); // 'percent' or 'amount'
    const [buttonState, setButtonState] = useState('default');
    const [voucherErrorMsg, setVoucherErrorMsg] = useState(null);
    const [localVoucherCode, setLocalVoucherCode] = useState('');
    const [showTooltip, setShowTooltip] = useState(null);

    const isCustomFormat = selectedFormat.value === 'custom';
    const getSizeDimensions = (size) => {
        const [width, height] = FORMATS[selectedFormat.value][size].split('x').map(d => parseFloat(d.replace(',', '.')));
        return { width, height };
    };

    // Helper function to find the largest available free size
    const getLargestFreeSizeWithVoucher = (discountType, discountValue) => {
        const sizeOrder = [...ALLOWED_SIZES].reverse(); // Largest to smallest, capped
        for (const size of sizeOrder) {
            if (!availableSizes[size]) continue; // Skip unavailable sizes

            const price = PRICES[size];
            let finalPrice = price;

            if (discountType === 'percent') {
                finalPrice = price * (1 - discountValue / 100);
            } else if (discountType === 'amount') {
                finalPrice = Math.max(0, price - discountValue / 100);
            }

            if (finalPrice === 0) {
                return size; // Found a free size
            }
        }

        return null; // No free size found
    };

    // Calculate which sizes are available based on maxDpis from Python
    const getAvailableSizes = () => {
        const availableSizes = {};

        if (!maxDpis) {
            // If maxDpis is not available, default to making only XXS available
            availableSizes.XXS = ALLOWED_SIZES.includes('XXS');
            ALLOWED_SIZES.filter(s => s !== 'XXS').forEach(size => {
                availableSizes[size] = false;
            });
            return availableSizes;
        }

        ALLOWED_SIZES
            .filter(size => FORMATS[selectedFormat.value][size] !== undefined)
            .forEach(size => {
                const dpi = maxDpis[size] || 0;
                // 200 DPI is the print-quality floor — backend probes only
                // 200..300 and stores 0 for anything below. Historical
                // entries with 100..199 also fall through here as
                // unavailable (we won't sell low-DPI prints).
                availableSizes[size] = dpi >= 200;
            });

        return availableSizes;
    };

    const availableSizes = getAvailableSizes();

    // The selected size is inherently free when its base price is 0 (XXS / XS / S
    // — see config/prices.js). Custom format always has a base price, so it's
    // not in this category. Used to hide the voucher field (no point applying a
    // discount to a 0€ price). The button label below uses the *effective*
    // price so a voucher-discounted-to-zero paid size also reads "Get Free Map".
    const baseUnitPrice = isCustomFormat ? (PRICES.custom ?? 0) : (PRICES[selectedSize] ?? 0);
    const isInherentlyFree = !isCustomFormat && baseUnitPrice === 0;
    let effectivePrice = baseUnitPrice;
    if (voucherDiscount) {
        if (voucherDiscountType === 'percent') {
            effectivePrice = baseUnitPrice * (1 - voucherDiscount / 100);
        } else if (voucherDiscountType === 'amount') {
            effectivePrice = Math.max(0, baseUnitPrice - voucherDiscount / 100);
        }
    }
    const isEffectivelyFree = effectivePrice === 0;

    // If the selected size is no longer available, reset to the first available size
    useEffect(() => {
        if (selectedSize && !availableSizes[selectedSize]) {
            // Find the first available size, preferring smaller sizes
            const firstAvailable = ALLOWED_SIZES.find(size => availableSizes[size]);
            if (firstAvailable) {
                setSelectedSize(firstAvailable);
            }
        }
    }, [availableSizes, selectedSize]);

    useEffect(() => {
        if (buttonState === 'error') {
            const timer = setTimeout(() => {
                setButtonState('default');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [buttonState]);

    // Auto-clear the inline voucher error after a few seconds — same pattern
    // as buttonState above. Stays a bit longer (4s) so the user has time to
    // actually read the reason before it disappears.
    useEffect(() => {
        if (!voucherErrorMsg) return;
        const timer = setTimeout(() => setVoucherErrorMsg(null), 4000);
        return () => clearTimeout(timer);
    }, [voucherErrorMsg]);

    const handlePurchase = async () => {
        try {
            if (isCustomFormat) {
                const originalDimensions = selectedFormat.originalDimensions || selectedFormat.dimensions;
                console.log("custom Selected size:", selectedSize, "Dimensions:", originalDimensions);
                await onSizeSelect('custom', originalDimensions, true, localVoucherCode);
            } else if (selectedSize) {
                const dimensions = getSizeDimensions(selectedSize);
                const needsRegeneration = selectedSize !== "S";
                if (isPortrait) {
                    [dimensions.width, dimensions.height] = [dimensions.height, dimensions.width];
                }
                console.log("Selected size:", selectedSize, "Dimensions:", dimensions, "Needs regeneration:", needsRegeneration);
                await onSizeSelect(selectedSize, dimensions, needsRegeneration, localVoucherCode);
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "An error occurred during the purchase!",
                variant: "destructive",
                duration: 10000,
            });
        }
    };

    const clearVoucher = () => {
        setLocalVoucherCode('');
        setVoucherCode('');
        setVoucherDiscount(null);
        setVoucherDiscountType(null);
        setButtonState('default');
        setVoucherErrorMsg(null);
    };

    const handleApplyVoucher = async () => {
        setIsApplyingVoucher(true);

        try {
            const response = await fetch(`${API_URL}/verify-voucher`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ code: localVoucherCode })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to verify voucher');
            }

            if (data.valid) {
                setVoucherCode(localVoucherCode);
                setVoucherDiscountType(data.discountType);

                let discountValue;
                if (data.discountType === 'percent') {
                    discountValue = data.discount;
                    setVoucherDiscount(data.discount);
                    toast({
                        title: "Voucher applied successfully",
                        description: `${data.discount}% discount will be applied to your purchase`,
                        variant: "success"
                    });
                } else if (data.discountType === 'amount') {
                    discountValue = data.discountCents;
                    setVoucherDiscount(data.discountCents);
                    toast({
                        title: "Voucher applied successfully",
                        description: `€${(data.discountCents / 100).toFixed(2)} discount will be applied to your purchase`,
                        variant: "success"
                    });
                }

                // Auto-select largest free available size if a size becomes free with this voucher
                const largestFreeSize = getLargestFreeSizeWithVoucher(data.discountType, discountValue);
                if (largestFreeSize) {
                    setSelectedSize(largestFreeSize);
                }

                setButtonState('success');
            } else {
                setButtonState('error');
                // Surface the server's reason inline under the input rather
                // than via toast — easier to associate with the field that
                // caused it.
                setVoucherErrorMsg(data.message || 'Invalid voucher code');
                setVoucherDiscount(null);
                setVoucherDiscountType(null);
            }
        } catch (error) {
            setButtonState('error');
            setVoucherErrorMsg(error.message || 'Failed to verify voucher');
            setVoucherDiscount(null);
        } finally {
            setIsApplyingVoucher(false);
        }
    };

    // Helper function to get DPI indicator styles.
    // Gauge range is 200..300 — anything below 200 is not purchasable,
    // so the indicator wouldn't render. The four colour zones now span
    // 200..225..250..275..300 for a "ok → great" gradient at print-quality
    // resolutions.
    const getDpiIndicatorStyles = (dpi) => {
        const minDpi = 200;
        const maxDpi = 300;
        const normalizedPosition = Math.max(0, Math.min(1, (dpi - minDpi) / (maxDpi - minDpi)));
        const percentage = normalizedPosition * 100;

        let color, bgColor;
        if (dpi < 225) {
            color = '#f97316'; // orange
            bgColor = '#fff7ed';
        } else if (dpi < 250) {
            color = '#84cc16'; // lime
            bgColor = '#f7fee7';
        } else if (dpi < 275) {
            color = '#22c55e'; // green
            bgColor = '#f0fdf4';
        } else {
            color = '#16a34a'; // deep green
            bgColor = '#f0fdf4';
        }

        return { percentage, color, bgColor };
    };

    const getButtonStyles = () => {
        switch (buttonState) {
            case 'success':
                return 'bg-green-500 hover:bg-green-600';
            case 'error':
                return 'bg-red-500 hover:bg-red-600 animate-shake';
            default:
                return '';
        }
    };

    // Check if any sizes are unavailable to show the warning
    const hasUnavailableSizes = Object.values(availableSizes).some(available => !available);

    return (
        <>
            <style>{blinkingStyle}</style>
            <div className="flex items-center justify-center px-4">
            <div className={`w-full max-w-md lg:max-w-xl shrink-0 border p-3 sm:p-4 lg:p-6 bg-gray-50 shadow-2xl rounded-xl relative
                    ${!finalImage ? 'opacity-10 pointer-events-none' : ''}
                `}>
                {/* Close button - positioned absolutely in top right */}
                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 z-10 p-1 hover:bg-gray-200 rounded-full transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                )}

                <h3 className="text-lg sm:text-xl text-center font-semibold mb-4">
                    {isCustomFormat ? 'Custom Format' : 'Select Size & Purchase'}
                </h3>

                {/* DPI/Processing warning */}
                {hasUnavailableSizes && (
                    <div className="flex items-center justify-center p-2 mb-3 bg-yellow-100 border border-yellow-300 rounded-xl">
                        <AlertTriangle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        <p className="text-xs sm:text-sm text-red-500 text-center">
                            Some sizes unavailable due to processing limitations for this area.
                        </p>
                    </div>
                )}

                {isCustomFormat ? (
                    <div className="p-3 sm:p-4 border rounded-xl mb-4">
                        <div className="font-medium text-sm sm:text-base">Custom Size</div>
                        <div className="text-xs sm:text-sm text-gray-500">
                            {selectedFormat.originalDimensions ? (
                                `${selectedFormat.originalDimensions.width}x${selectedFormat.originalDimensions.height} cm`
                            ) : (
                                `${selectedFormat.dimensions.originalWidth}x${selectedFormat.dimensions.originalHeight} cm`
                            )}
                        </div>
                        <div className="text-sm font-medium text-blue-600 mt-2">
                            {voucherDiscount && voucherDiscountType === 'percent'
                                ? <>
                                    <div className="line-through text-gray-400">€{PRICES.custom || PRICES.L}</div>
                                    <div className="text-green-600">€{((PRICES.custom || PRICES.L) * (1 - voucherDiscount / 100)).toFixed(2)}</div>
                                </>
                                : voucherDiscount && voucherDiscountType === 'amount'
                                ? <>
                                    <div className="line-through text-gray-400">€{PRICES.custom || PRICES.L}</div>
                                    <div className="text-green-600">€{Math.max(0, (PRICES.custom || PRICES.L) - voucherDiscount / 100).toFixed(2)}</div>
                                </>
                                : <span>€{PRICES.custom || PRICES.L}</span>
                            }
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-4 mb-2 w-full">
                        {ALLOWED_SIZES
                            .filter(size => FORMATS[selectedFormat.value][size] !== undefined)
                            .map((size) => {
                            const dimensions = getSizeDimensions(size);
                            const price = PRICES[size];
                            let discountedPrice = price;

                            if (voucherDiscount) {
                                if (voucherDiscountType === 'percent') {
                                    discountedPrice = price * (1 - voucherDiscount / 100);
                                } else if (voucherDiscountType === 'amount') {
                                    discountedPrice = Math.max(0, price - voucherDiscount / 100);
                                }
                            }

                            const dpi = maxDpis ? (maxDpis[size] || 0) : 0;
                            const isAvailable = availableSizes[size];
                            const isDisabled = !isAvailable;

                            return (
                                <div key={size} className="relative">
                                    <div className="relative">
                                        <button
                                            className={`w-full p-3 sm:p-4 border shadow-lg rounded-xl transition-colors text-left relative
                                                ${selectedSize === size ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                                                ${isDisabled ? 'opacity-40 cursor-help bg-gray-100' : 'hover:bg-gray-50'}`}
                                            onClick={() => {
                                                if (isDisabled) {
                                                    setShowTooltip(showTooltip === size ? null : size);
                                                } else {
                                                    setSelectedSize(size);
                                                }
                                            }}
                                        >
                                            <div className="font-medium text-sm sm:text-base">{size}</div>
                                            <div className="text-xs sm:text-sm text-gray-500">
                                                {dimensions.width}x{dimensions.height} cm
                                            </div>
                                            <div className="text-xs sm:text-sm font-medium mt-2">
                                                {voucherDiscount ? (
                                                    <>
                                                        <div className="flex space-x-2">
                                                            <div className="line-through text-gray-400">€{price}</div>
                                                            <div className="text-green-600">€{(discountedPrice).toFixed(2)}</div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-blue-600">€{price}</span>
                                                )}
                                                {isDisabled && (
                                                    <div className="text-xs text-orange-500 mt-2 flex items-center gap-1">
                                                        N/A
                                                        <Info className="h-3 w-3" />
                                                    </div>
                                                )}
                                            </div>
                                        </button>

                                        {/* Click-based tooltip for disabled state */}
                                        {isDisabled && showTooltip === size && (
                                            <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50">
                                                <div className="flex justify-between items-start">
                                                    <span>Size is not available, area is too small!</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowTooltip(null);
                                                        }}
                                                        className="ml-2 text-gray-300 hover:text-white"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                                <div className="absolute -top-1 left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* DPI Indicator Line */}
                                    {isAvailable && dpi > 0 && (
                                        <div
                                            className={`mt-2 px-2 py-1.5 rounded-lg transition-colors ${
                                                dpi < 300 && selectedSize === size ? 'bg-orange-100 rounded-xl' : 'bg-transparent'
                                            }`}
                                        >
                                            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                                                {(() => {
                                                    const { percentage, color, bgColor } = getDpiIndicatorStyles(dpi);
                                                    return (
                                                        <>
                                                            {/* Background gradient zones — 200..225..250..275..300 */}
                                                            <div className="absolute inset-0 flex">
                                                                <div className="w-1/4 bg-orange-300"></div>
                                                                <div className="w-1/4 bg-lime-300"></div>
                                                                <div className="w-1/4 bg-green-300"></div>
                                                                <div className="w-1/4 bg-green-500"></div>
                                                            </div>
                                                            {/* DPI indicator arrow */}
                                                            <div
                                                                className="absolute top-0 w-1 h-full transform -translate-x-1/2 z-10"
                                                                style={{
                                                                    left: `${percentage}%`,
                                                                    backgroundColor: color,
                                                                    // boxShadow: `0 0 0 0px white`
                                                                }}
                                                            ></div>
                                                            {/* Arrow pointing down */}
                                                            <div
                                                                className="absolute -bottom-1 w-0 h-0 transform -translate-x-1/2 z-10"
                                                                style={{
                                                                    left: `${percentage}%`,
                                                                    borderLeft: '3px solid transparent',
                                                                    borderRight: '3px solid transparent',
                                                                    borderTop: `4px solid ${color}`,
                                                                    filter: 'drop-shadow(0 0 1px white)'
                                                                }}
                                                            ></div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            {/* DPI value and quality text */}
                                            <div className="flex justify-between items-center mt-1">
                                                <span className="text-xs text-gray-400">200</span>
                                                <span
                                                    className={`text-xs font-medium ${
                                                        dpi < 300 && selectedSize === size ? 'blink-animation' : ''
                                                    }`}
                                                    style={{ color: getDpiIndicatorStyles(dpi).color }}
                                                >
                                                    {dpi} DPI
                                                </span>
                                                <span className="text-xs text-gray-400">300</span>
                                            </div>
                                        </div>
                                        // </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
                }

                {selectedSize && !isCustomFormat && maxDpis && (
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <p className="text-xs sm:text-sm text-center">
                            {(() => {
                                const dims = getSizeDimensions(selectedSize);
                                return (
                                    <span>
                                        Printable at {dims.width}x{dims.height}cm
                                    </span>
                                );
                            })()}
                        </p>
                    </div>
                )}

                {!isInherentlyFree && (
                <>
                <div className="flex flex-col items-center">
                    {voucherDiscount ? (
                        <>
                            <div className="text-green-600 font-normal mb-1 text-center text-sm">
                                {voucherDiscountType === 'percent'
                                    ? `${voucherDiscount}% discount`
                                    : `€${(voucherDiscount / 100).toFixed(2)} discount`
                                }
                            </div>
                            {voucherDiscountType === 'amount' && (() => {
                                // Remainder hint: when a fixed-amount voucher is bigger than
                                // the selected size's price, the leftover is auto-issued as
                                // a new voucher into the user's profile (see
                                // backend/blueprints/payment.py _redeem_voucher).
                                const sizePriceCents = Math.round((baseUnitPrice || 0) * 100);
                                const remainderCents = (voucherDiscount || 0) - sizePriceCents;
                                if (remainderCents <= 0) return null;
                                return (
                                    <div className="mb-1 text-center text-[11px] sm:text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 max-w-xs">
                                        €{(remainderCents / 100).toFixed(2)} will be saved as a new voucher in your profile.
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <div className="text-gray-500 font-normal mb-1 text-center text-xs sm:text-sm">
                            Do you have a discount voucher?
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-center mb-4">
                  <div className="flex justify-center items-center w-full">
                    <div className="flex gap-2 items-center w-full max-w-xs">
                        <div className="relative flex-1">
                            <Input
                                type="text"
                                placeholder="Voucher code"
                                value={localVoucherCode}
                                onChange={(e) => {
                                    setLocalVoucherCode(e.target.value);
                                    if (voucherErrorMsg) setVoucherErrorMsg(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && localVoucherCode && !isApplyingVoucher && !voucherDiscount) {
                                        handleApplyVoucher();
                                    }
                                }}
                                className={`w-full bg-gray-300/30 rounded-xl text-center focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400 text-sm transition-colors ${
                                    voucherErrorMsg ? 'border border-red-500 placeholder:text-red-400' : 'border-0'
                                }`}
                            />
                            {localVoucherCode && (
                                <button
                                    onClick={clearVoucher}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        {/* Wrapper for rainbow border effect */}
                        <div className="relative">
                            {localVoucherCode && !voucherDiscount && buttonState !== 'error' && (
                                <div
                                    className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-pink-500 via-red-500 via-yellow-500 via-green-500 via-blue-500 via-indigo-500 to-purple-500 animate-spin"
                                    style={{ animationDuration: '2s' }}
                                ></div>
                            )}
                            <Button
                                onClick={handleApplyVoucher}
                                disabled={!localVoucherCode || isApplyingVoucher || voucherDiscount}
                                size="icon"
                                className={`relative h-9 w-9 sm:h-10 sm:w-10 transition-colors ${getButtonStyles()}`}
                            >
                                {isApplyingVoucher ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : buttonState === 'error' ? (
                                    <X className="h-4 w-4" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                  </div>
                  {voucherErrorMsg && (
                    <p className="mt-1 text-xs text-red-500 text-center" role="alert">
                      {voucherErrorMsg}
                    </p>
                  )}
                </div>
                </>
                )}

                <div className="flex flex-col justify-center items-center">
                    <Button
                        onClick={handlePurchase}
                        disabled={(isCustomFormat ? false : !selectedSize)}
                        className="w-auto text-sm sm:text-base"
                    >
                        {isUnlocking ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                {isEffectivelyFree ? 'Get Free Map' : 'Purchase Map'}
                            </>
                        )}
                    </Button>
                    <span className="text-blue-500 text-xs sm:text-sm text-center mt-2 max-w-xs px-2">
                        Beware, this is a digital product! You will receive a high-resolution PNG of your map.
                    </span>
                </div>
            </div>
        </div>
        </>
    );
};

export default MapPurchaseSection;